import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
    ArrowLeft,
    Star,
    PackageSearch,
    Sparkles,
    Calendar,
    UserRound,
} from "lucide-react";
import { NEIGHBOR } from "../lib/testIds";
import { fetchNeighborProfile } from "../lib/api";
import ItemCard from "../components/ItemCard";
import ItemDetailModal from "../components/ItemDetailModal";

const formatDate = (iso) => {
    if (!iso) return null;
    try {
        return new Date(iso).toLocaleDateString("en-US", {
            month: "short",
            year: "numeric",
        });
    } catch {
        return null;
    }
};

const StarMeter = ({ value, size = "h-4 w-4" }) => (
    <div className="flex items-center gap-0.5">
        {[1, 2, 3, 4, 5].map((n) => (
            <Star
                key={n}
                className={`${size} ${
                    n <= Math.round(value || 0)
                        ? "fill-[#d6a73f] text-[#d6a73f]"
                        : "text-[#2e3220]/20"
                }`}
                strokeWidth={2}
            />
        ))}
    </div>
);

const StatTile = ({ label, value, sub, testId, accent }) => (
    <div
        data-testid={testId}
        className={`rounded-3xl border border-[#2e3220]/10 p-5 ${
            accent ? "bg-[#2e3220] text-[#F4F1EA]" : "bg-[#F4F1EA]"
        }`}
    >
        <p
            className={`text-[10px] font-bold uppercase tracking-widest ${
                accent ? "text-[#8a9450]" : "text-[#737a3d]"
            }`}
        >
            {label}
        </p>
        <p
            className={`mt-2 font-display text-3xl font-bold tracking-tight ${
                accent ? "text-[#F4F1EA]" : "text-[#2e3220]"
            }`}
        >
            {value}
        </p>
        {sub && (
            <p
                className={`mt-1 text-xs ${
                    accent ? "text-[#F4F1EA]/70" : "text-[#5a6142]"
                }`}
            >
                {sub}
            </p>
        )}
    </div>
);

const ReviewItem = ({ review }) => {
    const initials = (review.reviewer_name || "N").charAt(0).toUpperCase();
    return (
        <li
            data-testid={NEIGHBOR.reviewItem(review.id)}
            className="rounded-3xl border border-[#2e3220]/10 bg-[#F4F1EA] p-5"
        >
            <div className="flex items-start gap-3">
                {review.reviewer_picture ? (
                    <img
                        src={review.reviewer_picture}
                        alt={review.reviewer_name}
                        className="h-10 w-10 rounded-full object-cover"
                    />
                ) : (
                    <span className="flex h-10 w-10 items-center justify-center rounded-full bg-[#737a3d] font-display text-base font-bold text-[#F4F1EA]">
                        {initials}
                    </span>
                )}
                <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                        <p className="font-display font-bold text-[#2e3220]">
                            {review.reviewer_name || "Neighbor"}
                        </p>
                        <StarMeter value={review.rating} />
                    </div>
                    {review.item_name && (
                        <p className="text-xs text-[#5a6142]">
                            Rented{" "}
                            <span className="font-semibold text-[#2e3220]">
                                {review.item_name}
                            </span>
                        </p>
                    )}
                    {review.comment && (
                        <p className="mt-2 text-sm leading-relaxed text-[#2e3220]/85">
                            “{review.comment}”
                        </p>
                    )}
                    {formatDate(review.created_at) && (
                        <p className="mt-2 text-[10px] uppercase tracking-widest text-[#5a6142]/70">
                            {formatDate(review.created_at)}
                        </p>
                    )}
                </div>
            </div>
        </li>
    );
};

const NeighborProfile = () => {
    const { user_id } = useParams();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [notFound, setNotFound] = useState(false);
    const [selected, setSelected] = useState(null);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        setNotFound(false);
        fetchNeighborProfile(user_id)
            .then((d) => {
                if (cancelled) return;
                setData(d);
            })
            .catch((err) => {
                if (cancelled) return;
                if (err?.response?.status === 404) {
                    setNotFound(true);
                }
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [user_id]);

    if (loading) {
        return (
            <main
                data-testid={NEIGHBOR.loading}
                className="mx-auto max-w-6xl px-6 py-14 md:px-10 md:py-20"
            >
                <div className="h-32 animate-pulse rounded-3xl bg-[#E8E2D0]" />
                <div className="mt-6 grid gap-4 sm:grid-cols-4">
                    {Array.from({ length: 4 }).map((_, i) => (
                        <div
                            key={i}
                            className="h-28 animate-pulse rounded-3xl bg-[#E8E2D0]"
                        />
                    ))}
                </div>
            </main>
        );
    }

    if (notFound || !data) {
        return (
            <main
                data-testid={NEIGHBOR.notFound}
                className="mx-auto max-w-3xl px-6 py-20 text-center md:px-10"
            >
                <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-[#737a3d]/10 text-[#737a3d]">
                    <UserRound className="h-6 w-6" />
                </div>
                <h1 className="font-display text-3xl font-bold text-[#2e3220]">
                    Neighbor not found
                </h1>
                <p className="mx-auto mt-2 max-w-md text-sm text-[#5a6142]">
                    This profile may have been removed or never existed.
                </p>
                <Link
                    to="/browse"
                    className="btn-primary mt-6 inline-flex"
                >
                    <PackageSearch className="h-4 w-4" />
                    Back to browse
                </Link>
            </main>
        );
    }

    const initials = (data.name || "N").charAt(0).toUpperCase();
    const stats = data.stats || {};
    const memberSince = formatDate(data.member_since);

    return (
        <main
            data-testid={NEIGHBOR.root}
            className="relative z-10 mx-auto max-w-6xl px-6 py-14 md:px-10 md:py-20"
        >
            <Link
                to="/browse"
                data-testid={NEIGHBOR.backBtn}
                className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-widest text-[#737a3d] hover:text-[#2e3220]"
            >
                <ArrowLeft className="h-3.5 w-3.5" strokeWidth={2.5} />
                Browse
            </Link>

            <header className="mt-4 flex flex-col gap-5 sm:flex-row sm:items-center">
                {data.picture ? (
                    <img
                        src={data.picture}
                        alt={data.name}
                        data-testid={NEIGHBOR.avatar}
                        className="h-20 w-20 rounded-full object-cover ring-4 ring-[#737a3d]/15"
                    />
                ) : (
                    <span
                        data-testid={NEIGHBOR.avatar}
                        className="flex h-20 w-20 items-center justify-center rounded-full bg-[#737a3d] font-display text-2xl font-bold text-[#F4F1EA] ring-4 ring-[#737a3d]/15"
                    >
                        {initials}
                    </span>
                )}
                <div className="min-w-0">
                    <div className="inline-flex items-center gap-2 rounded-full border border-[#2e3220]/10 bg-[#F4F1EA] px-3 py-1.5">
                        <Sparkles className="h-3.5 w-3.5 text-[#737a3d]" strokeWidth={2.5} />
                        <span className="text-xs font-bold uppercase tracking-widest text-[#737a3d]">
                            Lendly neighbor
                        </span>
                    </div>
                    <h1
                        data-testid={NEIGHBOR.name}
                        className="mt-2 font-display text-4xl font-bold tracking-tight text-[#2e3220] sm:text-5xl"
                    >
                        {data.name}
                    </h1>
                    <div className="mt-2 flex flex-wrap items-center gap-3 text-xs text-[#5a6142]">
                        {memberSince && (
                            <span className="inline-flex items-center gap-1">
                                <Calendar className="h-3.5 w-3.5" />
                                Joined {memberSince}
                            </span>
                        )}
                        {stats.avg_rating != null && (
                            <span className="inline-flex items-center gap-1.5">
                                <StarMeter value={stats.avg_rating} size="h-3.5 w-3.5" />
                                {Number(stats.avg_rating).toFixed(1)} avg
                            </span>
                        )}
                    </div>
                </div>
            </header>

            <section className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                <StatTile
                    accent
                    testId={NEIGHBOR.statRating}
                    label="Avg rating"
                    value={
                        stats.avg_rating != null
                            ? Number(stats.avg_rating).toFixed(1)
                            : "—"
                    }
                    sub={
                        stats.reviews_count
                            ? `From ${stats.reviews_count} review${stats.reviews_count > 1 ? "s" : ""}`
                            : "No reviews yet"
                    }
                />
                <StatTile
                    testId={NEIGHBOR.statReviews}
                    label="Reviews"
                    value={stats.reviews_count || 0}
                    sub="From past renters"
                />
                <StatTile
                    testId={NEIGHBOR.statRentals}
                    label="Rentals completed"
                    value={stats.rentals_completed || 0}
                    sub="As the lender"
                />
                <StatTile
                    testId={NEIGHBOR.statItems}
                    label="Active listings"
                    value={stats.items_listed || 0}
                    sub="Available right now"
                />
            </section>

            <section className="mt-12">
                <h2 className="font-display text-2xl font-bold text-[#2e3220]">
                    Things {data.name?.split(" ")[0] || "they"} lends
                </h2>
                {data.listings?.length ? (
                    <div
                        data-testid={NEIGHBOR.listingsGrid}
                        className="mt-5 grid gap-5 sm:grid-cols-2 lg:grid-cols-3"
                    >
                        {data.listings.map((it) => (
                            <ItemCard
                                key={it.id}
                                item={it}
                                onView={(i) => setSelected(i)}
                            />
                        ))}
                    </div>
                ) : (
                    <p
                        data-testid={NEIGHBOR.listingsEmpty}
                        className="mt-5 rounded-3xl border border-dashed border-[#2e3220]/20 bg-[#F4F1EA] p-8 text-center text-sm text-[#5a6142]"
                    >
                        No active listings right now — check back soon.
                    </p>
                )}
            </section>

            <section className="mt-12">
                <h2 className="font-display text-2xl font-bold text-[#2e3220]">
                    Reviews
                </h2>
                {data.reviews?.length ? (
                    <ul
                        data-testid={NEIGHBOR.reviewsList}
                        className="mt-5 space-y-4"
                    >
                        {data.reviews.map((r) => (
                            <ReviewItem key={r.id} review={r} />
                        ))}
                    </ul>
                ) : (
                    <p
                        data-testid={NEIGHBOR.reviewsEmpty}
                        className="mt-5 rounded-3xl border border-dashed border-[#2e3220]/20 bg-[#F4F1EA] p-8 text-center text-sm text-[#5a6142]"
                    >
                        No reviews yet. Be the first neighbor to leave one.
                    </p>
                )}
            </section>

            <ItemDetailModal
                item={selected}
                open={!!selected}
                onOpenChange={(o) => !o && setSelected(null)}
            />
        </main>
    );
};

export default NeighborProfile;
